import React, { useEffect, useState } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, Plus, History, ShieldCheck } from 'lucide-react';
import { fetchRoutePermissions, RoutePerm } from '../../../services/permissionService';
import AddPermissionModal from '../../../components/Permission/AddPermissionModal';
import EditPermissionModal from '../../../components/Permission/EditPermissionModal';
import DeletePermissionModal from '../../../components/Permission/DeletePermissionModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const PermissionPage: React.FC = () => {
    const [permissions, setPermissions] = useState<RoutePerm[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedPermission, setSelectedPermission] = useState<RoutePerm | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedPermissionToDelete, setSelectedPermissionToDelete] = useState<RoutePerm | null>(null);
    const navigate = useNavigate();

    // Manejo de expiración de sesión
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('routepermission', userLevelId);

    const fetchPermissions = async () => {
        try {
            const data = await fetchRoutePermissions();
            const sortedData = data.sort((a, b) => a.id - b.id);
            setPermissions(sortedData);
        } catch (error) {
            console.error('Error al obtener permisos:', error);
        }
    };

    useEffect(() => {
        const load = async () => {
            try {
                if (perm?.can_read) {
                    const data = await fetchRoutePermissions();
                    setPermissions(data.sort((a, b) => a.id - b.id));
                } else {
                    setPermissions([]);
                }
            } catch {
                setPermissions([]);
            }
        };
        load();
    }, [perm]);

    const handleDeletePermission = (perm: RoutePerm) => {
        setSelectedPermissionToDelete(perm);
        setIsDeleteModalOpen(true);
    };

    // Lógica de paginación
    const totalPages = Math.ceil(permissions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedPermissions = permissions.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <Loading text="Cargando permisos..." />;

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">Gestión de Permisos</h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/permission" className="text-[#c1348b] hover:text-[#8f1f64]">Permisos</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
                        onClick={() => navigate('/permissions/history')}
                    >
                        <History className="w-5 h-5" />
                    </button>

                    {perm.can_create && (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center h-[39px] text-sm font-medium transition-colors min-w-[170px]"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Permisos
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <AddPermissionModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onPermissionAdded={fetchPermissions}
                />

                <EditPermissionModal
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setSelectedPermission(null);
                    }}
                    onPermissionUpdated={fetchPermissions}
                    permission={selectedPermission}
                />

                <DeletePermissionModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onPermissionDeleted={fetchPermissions}
                    permission={selectedPermissionToDelete}
                />

                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ruta</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roles Permitidos</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {displayedPermissions.map((permItem) => (
                                <tr key={permItem.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4 text-sm text-gray-900">{permItem.path}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        {permItem.allowed_roles.join(', ')}
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="flex gap-2">
                                            {perm.can_update && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedPermission(permItem);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                            )}
                                            {perm.can_delete && (
                                                <button
                                                    onClick={() => handleDeletePermission(permItem)}
                                                    className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {perm.can_read && permissions.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <ShieldCheck className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                No hay permisos disponibles
                                            </span>
                                            {perm.can_create && (
                                                <p className="text-gray-500 text-sm">
                                                    Agrega nuevos permisos para comenzar
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {perm.can_read && permissions.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={permissions.length}
                    />
                )}
            </div>
        </div>
    );
};

export default PermissionPage;